import type React from "react";
import type { ProfileBasedSuggestion } from "@shared/schemas/profile-based-suggestion.schemas";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { i18n } from "@frontend/i18n";

export interface ProfileSuggestionDisplayProps {
  readonly suggestion: ProfileBasedSuggestion;
}

/**
 * Renders the engine's why-connect paragraph.
 *
 * One shape, no fallback. The bullets this used to fall back to were the retired
 * headline-and-bullets arm, and the engine no longer offers a pick it cannot explain in
 * prose (PLA-1359) — so a missing paragraph means there is nothing to say here, not that
 * some other shape should be dug up.
 */
export function ProfileSuggestionDisplay({
  suggestion,
}: ProfileSuggestionDisplayProps): React.JSX.Element | null {
  const locale = i18n.getLocale();
  // `resolveNonEmpty`, not `resolve`: a blank paragraph must read as absent rather than
  // render an empty block.
  const paragraph = LocalizedTextUtils.resolveNonEmpty(
    suggestion.whyConnect,
    locale,
  );
  if (paragraph === null) return null;
  return (
    <p
      className="text-pds-xs text-foreground leading-normal whitespace-pre-line"
      data-testid="why-connect-paragraph"
    >
      {paragraph}
    </p>
  );
}
